import * as React from "react";
import { Box, ButtonBase, Paper, Stack, Typography } from "@mui/material";
import ElectricalServicesRoundedIcon from "@mui/icons-material/ElectricalServicesRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SchemaRoundedIcon from "@mui/icons-material/SchemaRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import EqualizerRoundedIcon from "@mui/icons-material/EqualizerRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { COLORS } from "../../theme/theme-options";

type ConfigSection = {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
};

const sections: ConfigSection[] = [
  {
    id: "tool-integrations",
    label: "Tool Integrations",
    href: "/config/tool-integrations",
    icon: <ElectricalServicesRoundedIcon fontSize="small" />,
  },
  {
    id: "alerts",
    label: "Alerts",
    href: "/config/alerts",
    icon: <WarningAmberRoundedIcon fontSize="small" />,
  },
  {
    id: "alert-schemas",
    label: "Alert Schemas",
    href: "/config/alert-schemas",
    icon: <SchemaRoundedIcon fontSize="small" />,
  },
  {
    id: "service-templates",
    label: "Service Templates",
    href: "/config/service-templates",
    icon: <LayersRoundedIcon fontSize="small" />,
  },
  {
    id: "kpis",
    label: "KPIs",
    href: "/config/kpis",
    icon: <EqualizerRoundedIcon fontSize="small" />,
  },
  {
    id: "users",
    label: "Users",
    href: "/config/users",
    icon: <PeopleRoundedIcon fontSize="small" />,
  },
];

const ConfigurationLayoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
        padding: 2,
        flexDirection: { xs: "column", md: "row" },
        alignItems: "flex-start",
      }}
    >
      <Paper
        variant="glass"
        sx={{
          display: { xs: "none", md: "block" },
          p: 0.75,
          width: { xs: "100%", md: 252 },
          maxWidth: { md: 252 },
          position: { md: "sticky" },
          top: { md: 84 },
        }}
      >
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={0.4}
          sx={{ overflowX: { xs: "auto", md: "visible" } }}
        >
          {sections.map((section) => {
            const isActive = pathname === section.href;

            return (
              <ButtonBase
                key={section.id}
                onClick={() => {
                  navigate(section.href);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1,
                  px: 1.5,
                  py: 0.9,
                  borderRadius: 1.5,
                  minWidth: { xs: 190, md: "100%" },
                  whiteSpace: "nowrap",
                  color: isActive ? COLORS.primary[300] : alpha(COLORS.grey[300], 0.95),
                  bgcolor: isActive
                    ? alpha(COLORS.primary[500], 0.12)
                    : "transparent",
                  border: isActive
                    ? `1px solid ${alpha(COLORS.primary[400], 0.25)}`
                    : "1px solid transparent",
                  "&:hover": {
                    bgcolor: alpha(COLORS.grey[800], 0.35),
                  },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    placeItems: "center",
                    color: isActive ? COLORS.primary[300] : alpha(COLORS.grey[500], 0.9),
                  }}
                >
                  {section.icon}
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: isActive ? 700 : 600 }}>
                  {section.label}
                </Typography>
              </ButtonBase>
            );
          })}
        </Stack>
      </Paper>

      <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default ConfigurationLayoutPage;
